import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ViewChild
} from '@angular/core';
import {ConfirmationService, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {ReportProfile} from '../../../model/archive/report-profile';
import {ReportProfileService} from '../../../services/archive/report-profile.service';
import {CrudBaseComponent} from '../../../crystal-elements/crud/crud-base.component';
import {FormFieldConfig} from '../../../model/api/form-field-config';
import {FieldRecord} from '../../../model/api/field-record';
import {FieldRequest} from '../../../model/api/field-request';
import {ReportTemplate} from '../../../model/archive/report-template';
import {ProfileAbsService} from '../../../services/archive/profile-abs.service';
import {FileUtils} from "../../../utils/file-utils";
import {ReportTaskService} from "../../../services/archive/report-task.service";
import {ReportTask} from "../../../model/archive/report-task";
import {CommonFieldService} from "../../../services/common/common-field.service";
import {CookieService} from "ngx-cookie-service";
import {CookieNames} from "../../../shared/cookie/cookie-names";
import {DatePipe, DecimalPipe} from "@angular/common";
import {CrudTableComponent} from "../../../crystal-elements/crud/crud-table.component";
import {NotificationService} from "../../../services/security/notification.service";
import {AppConfigService} from "../../../services/app-config.service";
import {ProfileTableRecordResult} from "../../../model/archive/profile-table-record-result";
import TYPE = FormFieldConfig.TYPE;
import VISIBILITY_RULE = ReportProfile.VISIBILITY_RULE;
import {UserInfoStorageService} from "../../../services/auth/user-info-storage.service";
import {CcStorageService} from "../../../services/common/cc-storage.service";
import {EMPTY, exhaustMap, switchMap, take, takeUntil, timer} from "rxjs";
import {catchError, filter, map, tap} from "rxjs/operators";
import {ApiFileResponse} from "../../../model/api/api-file-response";

@Component({
  template: '',
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
    DatePipe,
    DecimalPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProfileAbsComponent extends CrudBaseComponent<ReportProfile> {
  ORIGIN = ReportTask.Origin;

  visibilities: FieldRecord<any>[] = [];
  exportTemplates: FieldRecord<ReportTemplate>[] = [];
  exportFormats: FieldRecord<any>[] = [];
  exportDateModes: FieldRecord<any>[] = [];
  executionTimesValues: FieldRecord<any>[] = [];
  executionDaysOfMonthValues: FieldRecord<any>[] = [];
  transferMailTemplates: FieldRecord<any>[] = [];
  transferFileLocationsValues: FieldRecord<any>[] = [];
  profileTypes: FieldRecord<any>[] = [];

  origin: ReportTask.Origin;
  reportGroupFields: FormFieldConfig[] = [];
  userGroups: FieldRecord<any>[];

  inProgressTaskCount: number = 0

  selectedTypes: any = [];
  originSelectedTypes: any = [];

  @ViewChild('crudTable')
  public crudTable: CrudTableComponent;

  batchUpdateDialogVisible: boolean = false;
  batchFormRecord: ReportProfile;

  constructor(origin: ReportTask.Origin,
              cdref: ChangeDetectorRef,
              componentFactoryResolver: ComponentFactoryResolver,
              messageService: MessageService,
              protected confirmationService: ConfirmationService,
              translateService: TranslateService,
              protected reportProfileService: ReportProfileService,
              protected reportTaskService: ReportTaskService,
              protected notificationService: NotificationService,
              commonFieldService: CommonFieldService,
              protected appConfigService: AppConfigService,
              protected storageService: CcStorageService,
              protected userInfoStorageService: UserInfoStorageService) {
    super(cdref, messageService, confirmationService, translateService, commonFieldService);
    this.origin = origin;
    this.fieldId = 'name';
    this.newRecord = new ReportProfile();
    if (this.isProfileGroupLimited()) {
      this.newRecord.visibility = VISIBILITY_RULE.USER_GROUP;
      const userGroups = this.userInfoStorageService.getUserGroups();
      if (userGroups.length > 0)
        this.newRecord.userGroup = userGroups[0];
    }
    this.crudService = reportProfileService;

    if (this.storageService.check(this.origin + CookieNames.ARCHIVE_PROFILE_TYPES)) {
      this.originSelectedTypes = JSON.parse(this.storageService.get(this.origin + CookieNames.ARCHIVE_PROFILE_TYPES));
      this.selectedTypes = JSON.parse(this.storageService.get(this.origin + CookieNames.ARCHIVE_PROFILE_TYPES));
    }

    this.prepareForEdit = this.prepareRecordForEdit;
    this.prepareForClone = this.prepareRecordForClone;
  }

  ngOnInit() {
    super.ngOnInit();
    //   if (this.storageService.check(this.origin + CookieNames.ARCHIVE_PROFILE_TYPES)) {
    //     this.selectedTypes = JSON.parse(this.storageService.get(this.origin + CookieNames.ARCHIVE_PROFILE_TYPES));
    //   }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  protected initConfigs() {
    super.initConfigs();

    this.refreshTableColumns();

    const isProfileAdmin: boolean = this.isProfileAdmin();
    const isProfileGroupAdmin: boolean = this.isProfileGroupAdmin();

    this.formConfig = [];
    const groupFields = [];
    if (isProfileAdmin) {
      groupFields.push({
          field: 'active',
          column: 2,
          type: TYPE.CHECKBOX,
        },
        {
          field: 'visibility',
          required: true,
          column: 2,
          type: TYPE.DROPDOWN,
          options: () => this.visibilities
        },);
    } else if (isProfileGroupAdmin) {
      groupFields.push(
        {
          field: 'visibility',
          required: true,
          column: 2,
          type: TYPE.DROPDOWN,
          options: () => this.visibilities
        });
    }
    groupFields.push(
      {
        field: 'type',
        required: false,
        column: 2,
        type: TYPE.AUTOCOMPLETE,
        filter: true,
        options: (record) => this.profileTypes,
      },);

    if (isProfileAdmin || isProfileGroupAdmin) {
      groupFields.push(
        {
          field: 'userGroup',
          filter: true,
          column: 3,
          type: TYPE.DROPDOWN,
          options: () => this.userGroups,
          hidden: (record: ReportProfile) => record.visibility != VISIBILITY_RULE.USER_GROUP,
        },
        {
          column: 3,
          type: TYPE.NONE,
          hidden: (record: ReportProfile) => record.visibility == VISIBILITY_RULE.USER_GROUP,
        },);
    }

    groupFields.push(
      {
        type: TYPE.NONE,
        column: isProfileAdmin ? 3 : 10,
      }, {
        field: 'name',
        required: true,
        column: 6,
        type: TYPE.TEXT,
      },
      {
        field: 'title',
        required: true,
        column: 6,
        type: TYPE.TEXT,
      });
    this.formConfig.push({
      group: '',
      fields: groupFields
    });
    this.reportGroupFields = [{
      field: 'exportTemplate',
      required: true,
      column: 5,
      type: TYPE.DROPDOWN,
      filter: true,
      changed: event => this.refreshFilterMapValues(),
      options: () => this.exportTemplates
    },
      {
        field: 'exportFormat',
        required: true,
        column: 1,
        type: TYPE.DROPDOWN,
        options: () => this.exportFormats
      },
      {
        field: 'exportDateMode',
        required: true,
        column: 3,
        type: TYPE.DROPDOWN,
        options: () => this.exportDateModes,
        changed: event => {
          this.refreshFilterMapValues();
        }
      },
      {
        field: 'zipFile',
        column: 2,
        type: TYPE.CHECKBOX,
      },
      {
        type: TYPE.NONE,
        column: 1,
      },
      // {
      //   field: 'filterMap',
      //   column: 12,
      //   type: TYPE.MAPFIELD
      // },
      {
        field: 'filters',
        class: 'with-title height-limited',
        column: 12,
        type: TYPE.TABLEFORMFIELD,
        // validate: record => this.validateSplits(record),
        richFieldConfig: {
          // canDelete: formRecords => true,
          // canAdd: formRecords => true,
          // addNew: formRecords => this.addSplit(formRecords),
          fields: [
            {
              group: '',
              fields: [
                {
                  field: 'key',
                  column: 1,
                  type: TYPE.NONE,
                  style: record => 'width: 0',
                  icon: id => '',
                  iconValue: (event, formRecord) => {
                    // const template: FieldRecord<ReportTemplate> = this.exportTemplates.find(value => value.value.name==this.formRecord.exportTemplate);
                    // (template?template.value.parameterMap[formRecord.key]:'lala')
                    return formRecord.key;
                  },
                },
                {
                  field: 'value',
                  column: 1,
                  type: TYPE.TEXT,
                  rightButton: {
                    icon: 'fa-solid fa-ban',
                    command: (event, record) => {
                      this.deleteFilter(record.key);
                      this.formRecord.filterMap[record.key] = undefined;
                      this.refreshFilterMapValues();
                    },
                    class: 'button-field-small'
                  }
                }]
            },
          ]
        },
        changed: event => this.setFilterMaps()
      },
      {
        field: 'ignoreEmpty',
        column: 2,
        type: TYPE.CHECKBOX
      }
      , {
        field: 'differential',
        column: 2,
        type: TYPE.CHECKBOX,
      }
    ];
    this.formConfig.push({
      group: 'template',
      fields: this.reportGroupFields
    })
    if (isProfileAdmin) {
      this.formConfig.push({
        group: 'schedule',
        fields:
          [{
            field: 'executionTimesJS',
            column: 3,
            type: TYPE.MULTISELECT,
            options: () => this.executionTimesValues
          },
            {
              field: 'executionDaysOfMonthJS',
              column: 3,
              type: TYPE.MULTISELECT,
              options: () => this.executionDaysOfMonthValues
            },
            {
              field: 'executionCron',
              column: 3,
              type: TYPE.CRON,
            }]
      })
    }
    const transferGroupFields = [
      {
        field: 'transferMailTemplate',
        column: 3,
        type: TYPE.DROPDOWN,
        options: () => this.transferMailTemplates
      }, {
        field: 'transferMailSubject',
        column: 9,
        type: TYPE.TEXT,
      },
      {
        field: 'transferMailRecipients',
        column: 12,
        type: TYPE.MULTITEXT,
      }];
    if (isProfileAdmin) {
      transferGroupFields.push(
        {
          field: 'transferFileLocations',
          column: 6,
          type: TYPE.MULTISELECT,
          options: () => this.transferFileLocationsValues
        }
      );
    }
    this.formConfig.push({
      group: 'transfer',
      fields:
      transferGroupFields
    });
    if (isProfileAdmin) {
      this.formConfig.push({
        group: 'alert',
        fields:
          [{
            field: 'mailAlert',
            column: 2,
            type: TYPE.CHECKBOX
          }]
      });
    }
  }

  private refreshTableColumns() {
    let isProfileAdmin = this.isProfileAdmin();
    let isProfileGrpAdmin = this.isProfileGroupAdmin();

    this.tableConfig = [];
    if (isProfileAdmin) {
      this.tableConfig.push({
        field: 'active',
        notFilterable: true,
        notSortable: true,
        notResizable: true,
        type: 'BUTTON',
        class: value => 'centered',
        icon: value => value.active ? 'fas fa-toggle-on record-enabled' : 'fas fa-toggle-off record-disabled',
        command: (record: ReportProfile) => {
          this.activate(record);
        },
        mapId: value => ''
      });
    }
    this.tableConfig.push({
        field: 'name',
        width: 200
      },
      {
        field: 'title',
        width: 200,
        showToolTip: true
      },
      {
        field: 'type',
        filterConfig: {
          type: "text",
          matchMode: "equals",
          values: [...this.profileTypes.map(value => value.text)]
        }
      },
      {
        field: 'exportTemplate',
        width: 200
      },
      {
        field: 'exportDateMode',
      },
      {
        field: 'exportFormat',
        // icon:record => 'fa fa-file-'+(ReportProfile.EXPORT_FORMAT.XLS==record.exportFormat?'excel':'csv')
      },
      {
        field: 'zipFile',
        mapId: record => record.zipFile ? this.getTranslation('common.yes') : this.getTranslation('common.no')
      });
    if (isProfileAdmin) {
      this.tableConfig.push({
          field: 'executionDaysOfMonthJS',
          notFilterable: true,
          notSortable: true
        },
        {
          field: 'executionTimesJS',
          notFilterable: true,
          notSortable: true
        },
        {
          field: 'executionCron',
          notFilterable: true,
          notSortable: true
        });
    }
    this.tableConfig.push({
        field: 'transferMailSubject',
        width: 350,
        showToolTip: true
      },
      {
        field: 'transferMailTemplate',
      },
      {
        field: 'transferMailRecipients',
        width: 150,
        notSortable: true,
        showToolTip: true
      });
    if (isProfileAdmin) {
      this.tableConfig.push(
        {
          field: 'visibility',
          width: 80
        },
        {
          field: 'userGroup',
          width: 130
        },
        {
          field: 'transferFileLocations',
          width: 130,
          showToolTip: true
        },
        {
          field: 'mailAlert',
          mapId: record => record.mailAlert ? this.getTranslation('common.yes') : this.getTranslation('common.no')
        });
    } else if (isProfileGrpAdmin) {
      this.tableConfig.push(
        {
          field: 'visibility',
          width: 80
        },
        {
          field: 'userGroup',
          width: 130
        });
    }
  }

  protected postInitConfigs() {
    this.headerButtonConfig.splice(2, 0,
      {
        'tooltip': this.translateService.instant('page.' + this.transPartKey + '.table.batch_update_button'),
        'icon': 'fas fa-pen-to-square',
        class: ' p-button-help',
        command: records => this.batchUpdate(),
        enabled: () => !this.hasOneSelection() && this.hasMultipleSelection()
      },
    );
    this.headerButtonConfig.push(
      {
        separator: true,
      },
      {
        'tooltip': this.translateService.instant('page.' + this.transPartKey + '.table.download_button'),
        'icon': 'fas fa-file-download',
        class: ' p-button-success',
        command: records => this.download(),
        enabled: () => this.hasOneSelection()
      },
      {
        'tooltip': this.translateService.instant('page.' + this.transPartKey + '.table.execute_button'),
        'icon': 'fas fa-play',
        class: ' p-button-info',
        command: records => this.execute(),
        enabled: () => this.hasMultipleSelection()
      }
    );
    this.headerButtonConfig = [...this.headerButtonConfig];
    this.contextMenuConfig.splice(1, 0,
      {
        id: 'batch-update',
        label: this.translateService.instant('page.' + this.transPartKey + '.table.batch_update_button'),
        icon: 'fas fa-pen-to-square',
        command: () => {
          this.batchUpdate();
        }
      }
    );
    this.contextMenuConfig.unshift(
      {
        id: 'download',
        label: this.translateService.instant('page.' + this.transPartKey + '.table.download_button'),
        icon: 'fas fa-file-download',
        command: () => {
          this.download();
        }
      },
      {
        id: 'execute',
        label: this.translateService.instant('page.' + this.transPartKey + '.table.execute_button'),
        icon: 'fas fa-play',
        command: () => {
          this.execute();
        }
      },
      {
        separator: true
      },
    );
    this.contextMenuConfig = [...this.contextMenuConfig];
  }

  protected initData() {
    super.initData();

    this.settingsFormLoadingProgressValue = 0;
    if (this.isProfileAdmin()) {
      this.visibilities = [];
      for (const key in ReportProfile.VISIBILITY_RULE) {
        this.visibilities.push(new FieldRecord(key, key.toLowerCase()));
      }
    } else if (this.isProfileGroupAdmin()) {
      this.visibilities = [];
      [ReportProfile.VISIBILITY_RULE.PUBLIC, ReportProfile.VISIBILITY_RULE.USER_GROUP].forEach(key => {
        this.visibilities.push(new FieldRecord(key, key.toLowerCase()));
      });
    }
    this.exportFormats = [];
    for (const key in ReportProfile.EXPORT_FORMAT) {
      this.exportFormats.push(new FieldRecord(key, key.toLowerCase()));
    }
    this.exportDateModes = [];
    for (const key in ReportProfile.EXPORT_DATE_MODE) {
      this.exportDateModes.push(new FieldRecord(key, key.toLowerCase()));
    }
    this.executionTimesValues = [];
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 10; j++) {
        const time1 = i + '' + j + ':00';
        this.executionTimesValues.push(new FieldRecord(time1, time1));
        const time2 = i + '' + j + ':30';
        this.executionTimesValues.push(new FieldRecord(time2, time2));
        if ('23' === i + '' + j) {
          break;
        }
      }
    }
    this.executionDaysOfMonthValues = [];
    for (let i = 1; i <= 31; i++) {
      this.executionDaysOfMonthValues.push(new FieldRecord(i + '', i + ''));
    }
    (this.crudService as ProfileAbsService).getExportTemplatesProfile(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.exportTemplates = this.getFields(fieldResult);
      // console.log("--------> exportTemplates "+JSON.stringify(fieldResult));
      this.refreshFilterMapValues();
      this.settingsFormLoadingProgressValue += 100 / 5;
    });
    (this.crudService as ProfileAbsService).getMailTemplates(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.transferMailTemplates = this.getFields(fieldResult);
      this.settingsFormLoadingProgressValue += 100 / 5;
    });
    (this.crudService as ProfileAbsService).getFileLocations(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.transferFileLocationsValues = this.getFields(fieldResult);
      this.settingsFormLoadingProgressValue += 100 / 5;
    });
    if (this.isProfileAdmin() || this.isProfileGroupAdmin()) {
      this.commonFieldService.getUserGroups(FieldRequest.DEFAULT()).subscribe(fieldResult => {
        this.userGroups = this.getFields(fieldResult);
        this.settingsFormLoadingProgressValue += 100 / 5;
      });
    } else {
      this.settingsFormLoadingProgressValue += 100 / 5;
    }
    this.refreshTypes();
  }

  private refreshTypes() {
    if (this.settingsFormLoadingProgressValue == 100)
      this.settingsFormLoadingProgressValue = 4 * 100 / 5;
    let request: FieldRequest = FieldRequest.DEFAULT();
    // request.search3 = [ReportProfile.VISIBILITY_RULE.PUBLIC, ReportProfile.VISIBILITY_RULE.ADMIN
    //   , ReportProfile.VISIBILITY_RULE.USER, ReportProfile.VISIBILITY_RULE.PROFILES, ReportProfile.VISIBILITY_RULE.USER_GROUP];
    // request.search4 = [ReportTemplate.VISIBILITY_RULE.PUBLIC, ReportTemplate.VISIBILITY_RULE.ADMIN
    //   , ReportTemplate.VISIBILITY_RULE.PROFILES, ReportTemplate.VISIBILITY_RULE.FORMS];
    // (this.crudService as ProfileAbsService).getProfileTypes(request).subscribe(fieldResult => {
    //   this.profileTypes = this.getFields(fieldResult);
    //   // console.log("------> profileTypeItems "+JSON.stringify(this.profileTypeItems));
    //   this.refreshTableColumns();
    //   // let defaultAllType = FieldRecord.DEFAULT();
    //   // defaultAllType.text = this.translateService.instant('common.all');
    //   // defaultAllType.id = ExchangeDetail.ALL_TEXT;
    //   // this.profileTypes.unshift(defaultAllType);
    //   // console.log("------> profileTypes " + JSON.stringify(this.profileTypes))
    this.profileTypeChanged();
    this.settingsFormLoadingProgressValue += 100 / 5;
    //   this.detectChanges();
    if (this.crudTable)
      this.crudTable.reloadFieldValueMap();
    // });
  }

  private batchUpdate() {
    if (this.selectedRecords && this.selectedRecords.length > 0) {
      this.batchFormRecord = {...this.selectedRecords[0]};
      if (!this.batchFormRecord.transferMailRecipients)
        this.batchFormRecord.transferMailRecipients = [];
      if (!this.batchFormRecord.transferFileLocations)
        this.batchFormRecord.transferFileLocations = [];
      this.selectedRecords.forEach(value => {
        if (value.transferMailRecipients) {
          value.transferMailRecipients.forEach(valueEmail => {
            if (!this.batchFormRecord.transferMailRecipients.includes(valueEmail)) {
              this.batchFormRecord.transferMailRecipients.push(valueEmail);
            }
          });
        }
        if (value.transferFileLocations) {
          value.transferFileLocations.forEach(valueLoc => {
            if (!this.batchFormRecord.transferFileLocations.includes(valueLoc)) {
              this.batchFormRecord.transferFileLocations.push(valueLoc);
            }
          });
        }
      });
    }

    this.batchUpdateDialogVisible = true;
  }

  download() {
    if (this.selectedRecords && this.selectedRecords.length === 1) {
      this.formRecord = this.selectedRecords[0];
    }
    if (this.formRecord) {
      this.tableLoading = true;
      // (this.crudService as ProfileAbsService).downloadAsync(this.origin, this.formRecord.name).subscribe(apiValidation => {
      (this.crudService as ProfileAbsService).downloadForProfileAsync(this.origin, this.formRecord).subscribe(apiValidation => {
        if (apiValidation.success) {
          this.startCheckingReportTask(apiValidation.item, true);
        } else {
          this.errorMessage(apiValidation.message);
          this.tableLoading = false;
        }
      });
    }
  }

  execute() {
    if (this.selectedRecords && this.selectedRecords.length > 0) {
      this.tableLoading = true;
      const profileNames = this.selectedRecords.map(value => value.name);
      this.confirmationService.confirm({
        header: this.translateService.instant('page.' + this.transPartKey + '.table.execute_button') + ' ' + profileNames.join(','),
        message: this.translateService.instant('page.' + this.transPartKey + '.message.confirm_execute'),
        icon: 'fas fa-exclamation-triangle',
        acceptLabel: this.translateService.instant('page.' + this.transPartKey + '.table.execute_button'),
        acceptButtonStyleClass: 'p-button-success p-button-sm',
        rejectLabel: this.translateService.instant('common.dialog.cancel'),
        rejectButtonStyleClass: 'p-button-warning p-button-sm',
        accept: () => {
          (this.crudService as ProfileAbsService).executeAsync(this.origin, profileNames).subscribe(apiValidation => {
            if (apiValidation.success) {
              apiValidation.item.forEach(value => this.startCheckingReportTask(value, false));
            } else {
              this.errorMessage(apiValidation.message);
            }
            this.tableLoading = false;
          });
        },
        reject: () => {
          this.tableLoading = false;
        }
      });
    }
  }


  startCheckingReportTask(taskId: string, download: boolean): void {
    const intervalMs = this.appConfigService.reportCheckInterval;
    const limit = this.appConfigService.reportCheckCallLimit;

    // count this task once (not once per poll)
    this.inProgressTaskCount++;

    this.tableLoading = true;

    timer(0, intervalMs)
      .pipe(
        takeUntil(this.destroy$),
        take(limit),

        // One check at a time, no overlap
        exhaustMap(() =>
          this.reportTaskService.check(taskId).pipe(
            catchError(err => {
              this.errorMessage(String(err));
              this.tableLoading = false;
              this.markForCheck();
              return EMPTY;
            }),
          ),
        ),

        tap(v => {
          if (!v.success) {
            this.errorMessage(v.message);
            this.tableLoading = false;
            this.markForCheck();
          }
        }),
        filter(v => v.success),

        map(v => v.item as ReportTask | null),
        filter((t): t is ReportTask => !!t),

        // stop when not IN_PROGRESS
        filter(task => task.state !== ReportTask.STATE.IN_PROGRESS),
        take(1),

        // terminal handling
        switchMap(task => {
          if (task.state === ReportTask.STATE.ERROR) {
            this.errorMessage(task.message);
            this.tableLoading = false;
            this.markForCheck();
            return EMPTY;
          }

          if (task.state !== ReportTask.STATE.TREATED) {
            this.errorMessage(`Report task ${taskId} ended in unexpected state: ${task.state}`);
            this.tableLoading = false;
            this.markForCheck();
            return EMPTY;
          }

          // TREATED
          if (!download) {
            this.successMessage(this.translateService.instant(`page.${this.transPartKey}.message.profile_executed`));
            this.tableLoading = false;
            this.markForCheck();
            return EMPTY;
          }

          // download=true -> download file (also no overlap)
          return this.reportTaskService.download(taskId).pipe(
            catchError(err => {
              this.errorMessage(String(err));
              this.tableLoading = false;
              this.markForCheck();
              return EMPTY;
            }),
          );
        }),

        tap(fileResp => {
          if (!fileResp.success) {
            // you used successMessage here originally; keeping it
            this.successMessage(fileResp.message);
            this.tableLoading = false;
            this.markForCheck();
          }
        }),
        filter(fileResp => fileResp.success),
      )
      .subscribe((fileResp: ApiFileResponse) => {
        // download success
        FileUtils.download(fileResp);
        this.tableLoading = false;
        this.markForCheck();
      });
  }


  prepareRecordForClone(): void {
    if (this.formRecord) {
      this.formRecord.name = this.formRecord.name + '_copy';
      this.formRecord.originName = undefined;
    }
  }

  prepareRecordForEdit(): void {
    if (this.formRecord) {
      this.formRecord.originName = this.formRecord.name;
    }
  }

  private activate(record: ReportProfile) {
    this.formRecord = record;
    this.prepareRecordForEdit();
    this.formRecord.active = !this.formRecord.active;
    this.save();
  }

  public onSaved() {
    this.refreshTable();
  }

  profileTypeChanged() {
    if (this.selectedTypes)
      if (this.tableCurrentRequest
        && JSON.stringify(this.originSelectedTypes) != JSON.stringify(this.selectedTypes)) {
        this.storageService.set(this.origin + CookieNames.ARCHIVE_PROFILE_TYPES, JSON.stringify(this.selectedTypes));
        // {
        //   this.tableCurrentRequest.filters.splice(this.tableCurrentRequest.filters.indexOf(typeFilter), 1);
        // }
        this.tableCurrentRequest.first = 0;
        this.crudTable.ccTable.setFiltersAndSort(this.tableCurrentRequest);
        this.originSelectedTypes = JSON.parse(this.storageService.get(this.origin + CookieNames.ARCHIVE_PROFILE_TYPES));
        // setTimeout(() => {
        this.tableCurrentRequestChanged();
        this.refreshTable();
        // });
      }
  }

  refreshTable() {
    if (this.tableCurrentRequest) {
      this.loadRecords(this.tableCurrentRequest);
      this.crudTable.refreshContextMenu();
    }
  }

  clearProfileType() {
    this.selectedTypes.splice(0);
    this.selectedTypes = [...this.selectedTypes];
    // this.profileTypeChanged();
    this.storageService.set(this.origin + CookieNames.ARCHIVE_PROFILE_TYPES, JSON.stringify(this.selectedTypes));
    setTimeout(() => this.profileTypeChanged());
  }

  tableCurrentRequestChanged() {
    if (this.tableCurrentRequest) {
      let typeFilter = this.tableCurrentRequest.filters2.find(value => value.field == 'type');
      if (!typeFilter) {
        typeFilter = {
          field: 'type',
          matchMode: 'in',
          operator: 'and'
        };
        this.tableCurrentRequest.filters2.push(typeFilter);
      }
      // else if (this.selectedTypes.length == 0) {
      //   this.tableCurrentRequest.filters.splice(this.tableCurrentRequest.filters.findIndex(value => value.field == 'type'), 1);
      // }
      if (this.selectedTypes.length > 0) {
        typeFilter.value = this.selectedTypes.map(value => value.id).join(',');
      } else {
        typeFilter.value = undefined;
        const index = this.tableCurrentRequest.filters2.findIndex(value => value.field == 'type');
        this.tableCurrentRequest.filters2.splice(index, 1);
      }
      this.refreshFilterMapValues();
    }
  }

  private refreshFilterMapValues() {
    if (this.formRecord) {
      //set custom filters
      if (!this.formRecord.filters) {
        this.formRecord.filters = [];
        if (this.formRecord.filterMap) {
          for (const key in this.formRecord.filterMap) {
            const existingValue = this.formRecord.filterMap[key];
            if (existingValue) {
              this.formRecord.filters.push({key: key, value: existingValue})
            }
          }
        }
      }

      if (ReportProfile.EXPORT_DATE_MODE.CUSTOM == this.formRecord.exportDateMode) {
        if (!this.formRecord.filters.find(value => value.key === 'customStartDate'))
          this.formRecord.filters.push({key: 'customStartDate', value: new Date().toISOString().slice(0, 10)});
        if (!this.formRecord.filters.find(value => value.key === 'customEndDate'))
          this.formRecord.filters.push({key: 'customEndDate', value: new Date().toISOString().slice(0, 10)});
        // if (!this.formRecord.filterMap['customStartDate'])
        //   this.formRecord.filterMap['customStartDate'] = new Date().toISOString().slice(0, 10);
        // if (!this.formRecord.filterMap['customEndDate'])
        //   this.formRecord.filterMap['customEndDate'] = new Date().toISOString().slice(0, 10);
      }
      //clean empty value
      const emptyFilterKeys = [];
      for (const index in this.formRecord.filters) {
        const filter = this.formRecord.filters[index];
        if (!filter.value)
          emptyFilterKeys.push(filter.key);
      }
      emptyFilterKeys.forEach(value => this.deleteFilter(value));
      // for (const key in this.formRecord.filterMap) {
      //   const existingValue = this.formRecord.filterMap[key];
      //   if (!existingValue) {
      //     delete this.formRecord.filterMap[key];
      //   }
      // }
      //fill with available filters
      const fieldTemplate = this.exportTemplates.find(value => value.id === this.formRecord.exportTemplate);
      if (fieldTemplate && fieldTemplate.value && fieldTemplate.value.parameterMap) {
        let hasChanged: boolean = false;
        for (const key in fieldTemplate.value.parameterMap) {
          const existingFilter: number = this.formRecord.filters.find(value => value.key === key);
          if (!existingFilter) {
            this.formRecord.filters.push({key: key, value: ''});
            hasChanged = true;
          }

          // const existingValue = this.formRecord.filterMap[key];
          // if (!existingValue) {
          //   this.formRecord.filterMap[key] = '';
          //   hasChanged = true;
          // }
        }
      }
      queueMicrotask(() => {
        this.setFilterMaps();
        if (this.crudTable)
          this.crudTable.ccFormDialog.reloadFieldValueMap();
      });
    }
  }

  setFilterMaps() {
    this.formRecord.filterMap = {};
    if (this.formRecord.filters) {
      this.formRecord.filters.forEach(value => {
        if (value.value)
          this.formRecord.filterMap[value.key] = value.value;
      })
    }
  }

  private deleteFilter(key: string) {
    const existingValueIndex: number = this.formRecord.filters.findIndex(value => value.key === key);
    if (existingValueIndex >= 0) {
      this.formRecord.filters.splice(existingValueIndex, 1);
    }
  }

  onFormShow() {
    this.refreshFilterMapValues();
  }

  refreshContextMenu() {
    this.contextMenuConfig.forEach(menu => {
      if (menu.id === 'download') {
        menu.disabled = !this.hasOneSelection()
      } else if (menu.id === 'execute') {
        menu.disabled = !this.hasMultipleSelection()
      } else if (menu.id === 'batch-update') {
        menu.disabled = this.hasOneSelection() || !this.hasMultipleSelection()
      }
    });
    this.contextMenuConfig = [...this.contextMenuConfig];
    this.markForCheck();
    // this.crudTable.ccTable.tableContextMn.re;
  }

  protected loadTypes(tableRecordResult: ProfileTableRecordResult) {
    this.profileTypes.splice(0);
    this.profileTypes.push(...tableRecordResult.recordTypes);
    this.markForCheck();
  }

  protected isProfileGroupAdmin(): boolean {
    return false;
  }

  protected isProfileGroupLimited(): boolean {
    return false;
  }

  protected isProfileAdmin(): boolean {
    return super.isAdmin() || this.isProfileScheduleAdmin();
  }

  protected isProfileScheduleAdmin(): boolean {
    return false;
  }
}
